import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import {
  type ControlledDrugRegisterEntry,
  useCorrectRegisterEntry,
  useWitnessCandidates,
} from '@/features/registers/api';
import { isApiError } from '@/lib/api';
import styles from './ControlledDrugsPage.module.css';

/**
 * RL-1635: "Correct entry": the quantity the entry should have read and why,
 * with an optional witness. The API adds a linked correction entry and moves
 * the stock by the difference; the original entry is never changed.
 */
export function CorrectEntryDialog({
  entry,
  onClose,
}: {
  entry: ControlledDrugRegisterEntry;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const correct = useCorrectRegisterEntry();
  const witnesses = useWitnessCandidates(true);
  const current = entry.qtyIn || entry.qtyOut;
  const [quantity, setQuantity] = useState(String(current));
  const [reason, setReason] = useState('');
  const [witnessId, setWitnessId] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const qty = Number(quantity);
  // RL-1942: the witness is required.
  const valid =
    quantity.trim() !== '' &&
    Number.isInteger(qty) &&
    qty >= 0 &&
    qty <= 100000 &&
    reason.trim().length >= 5 &&
    witnessId !== '';

  const save = () => {
    setRefusal(null);
    void correct
      .mutateAsync({
        entryId: entry.id,
        body: {
          quantity: qty,
          reason: reason.trim(),
          witnessId,
        },
      })
      .then((r) => {
        toast.show({
          tone: 'success',
          title: t('dispensary.cdCorrected', {
            entry: r.correction.entryNo,
          }),
        });
        onClose();
      })
      .catch((err: unknown) => {
        if (isApiError(err) && (err.status === 422 || err.status === 404))
          setRefusal(err.message);
        else
          toast.show({
            tone: 'error',
            title: t('dispensary.cdCorrectFailed'),
            error: err,
          });
      });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={!correct.isPending}
      title={t('dispensary.cdCorrectTitle', {
        entry: entry.entryNo,
        medicine: entry.medicine.name,
      })}
      footer={
        <>
          <Button disabled={correct.isPending} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={correct.isPending}
            disabled={!valid}
            onClick={save}
          >
            {t('dispensary.cdCorrectSave')}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <p className={styles.sub}>
          {t('dispensary.cdCorrectIntro', {
            quantity: current,
            direction: entry.qtyIn
              ? t('dispensary.controlledDrugsIn')
              : t('dispensary.controlledDrugsOut'),
          })}
        </p>
        {refusal && (
          <p className={styles.refusal} role="alert">
            {refusal}
          </p>
        )}
        <Field label={t('dispensary.cdCorrectQuantity')}>
          <Input
            type="number"
            min={0}
            max={100000}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </Field>
        <Field
          label={t('dispensary.cdCorrectReason')}
          hint={t('dispensary.cdCorrectReasonHint')}
        >
          <Input
            value={reason}
            maxLength={300}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        <Field label={t('dispensary.cdWitness')}>
          <Select
            value={witnessId}
            placeholder={t('dispensary.cdChooseWitness')}
            options={(witnesses.data ?? []).map((w) => ({
              value: w.id,
              label: w.fullName,
            }))}
            onChange={(event) => setWitnessId(event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
